import {IEndPoint, IO} from "lib/commons/io/io";
import {CoreUniverse, ICoreUniverseEnv, IUniversePointer} from "lib/core/universe";
import {IReg, IRegPointer, IUiEnv, REG} from "lib/commons/registry";
import {CDM} from "lib/commons/utils/cdm";
import {Action} from "lib/commons/actions";
import {DOM, JSX, ShadowJsx} from "lib/commons/xml/dom";
import {Signboard} from "lib/commons/utils/signboard";
import {LANG} from "lib/commons/lang";
import {POPUP} from "back/commons/widgets/popups";
import {ERROR} from "lib/core/errorReport";
import {GridSmall, OGridSmallInit} from "back/commons/widgets/grid-tags";
import {GridColDef} from "back/commons/widgets/grid-core";
import {CellBuilderBarActions, CellBuilderDate, CellBuilderEnum, GridDataHolderJsonArray, GridDataRowJson} from "back/commons/widgets/grid-libs";
import {CellBuilderAccount} from "back/core/widgets/usersGrid";
import {FORMS} from "lib/commons/forms";
import {AREAS, IArea, IControlAreaContext} from "lib/commons/areas";
import {JobPlanificatorInputArea} from "back/core/widgets/jobsInput";

type dynAny<C, T = any> = (ctx: C) => T;

/** **/

export interface OExecutorInUniverseConfig {
	/** Si la propriété est présente ET null, celà indique que l'executor ne doit pas être instancié (ie fonction non active dans ce contexte) **/
	executor?: OExecutorSrvConfig
}

export interface IExecutorInUniversePointer extends IUniversePointer {
	universe: CoreUniverse & IExecutorInUniverse
}

export interface IExecutorInUniverse {
	executor: ExecutorSrv;
}

/**
 * Service "executor"
 */
export class ExecutorSrv {
	constructor(public readonly config: OExecutorSrvConfig) {}

	/** Evalue les features */
	hasFeature(feat: 'scheduling' | 'freezable'): boolean | undefined {
		return this.config.features ? this.config.features[feat] : undefined;
	}
}

export interface OExecutorSrvConfig {
	executorUrl?: IEndPoint;
	features?: {
		/** si true, activation des fonctions de programmation */
		scheduling?: boolean,
		/** si true, activation de la capacité de freeze de l'éxécution des jobs */
		freezable?: boolean,
	}
}

export function configExecutorSrv(webFrameUrl: IEndPoint, config?: OExecutorSrvConfig): OExecutorSrvConfig {
	if (!config) config = {} as OExecutorSrvConfig;
	if (!config.executorUrl) config.executorUrl = webFrameUrl.resolve("u/executor/");
	return config;
}

export interface IJobPointer {
	job: JJob
}

export interface JJobBase {
	id: string,
	jobSgn?: string,
	created: number,
	createdBy: string,
	lastQueued: number,
	lastStatus: EJobStatus,
	/* Actif que dans l'implémentation odb */
	planned?: number,
}

export interface JJob extends JJobBase {
	tries?: JJobPropsTry[],
	jobProps?: Jsonisable,
}

export interface JJobPropsTry {
	started: number,
	finished: number,
	by?: string,
	status: EJobStatus,
	result?: {
		success: EJobSucess,
		system?: Jsonisable,
	},
	error: string,
}


/** Etat d'une session WebSocket */
export const enum EJobSucess {
	/** job abouti. */
	finished = 'finished',
	/** Echec définitif, inutile de lancer une nouvelle tentative. */
	finalFailure = 'finalFailure',
	/** Echec de cette tentative. Peut être retenté ultérieurement. */
	attemptFailed = 'attemptFailed'
}


/** liste ordonnée de jobs */
export interface JListJobs {
	jobs: JJobBase[],
}

/** Informations sur les jobs du service
 * @see CDACTION_GetStats
 */
export interface JJobsStats {
	countInQueue: number,
	countFailed: number,
	frozen?: boolean,
}

/**
 * Interface des actionContext pour les actions relatives à un ensemble de jobs
 */
export interface IJobsCtx {
	jobs: JJobBase[],
	reg: IReg<IExecutorInUniversePointer & IUiEnv>,
}

export const enum EJobStatus {
	'waiting' = 'waiting',
	'launched' = 'launched',
	'pending' = 'pending',
	'done' = 'done',
	'failed' = 'failed',
	'planned' = 'planned',
}

/** Critères de filtres des jobs */
export interface JJobCriterion {
	/** Identifiant du propriétaire
	 * NOTE : sans effet dans findMyJobs */
	owner?: string,
	regexpSgn?: string,
	statuses?: EJobStatus[],
	maxCount?: number,
	/** Recherche spécifique dans la file d'attente, avec acces par offset et on par date */
	inQueue?: boolean,
	from?: number,
	/** Recherche par date */
	after?: number,
	before?: number,
}

// ###
// # EXECUTOR

export namespace EXECUTOR {

	/** Liste de promises de UiJobFactory, ou de fctions retournant cette promise */
	export const JOBS_FACTORIES_LIST = "executor:jobs:factories";

	/**
	 * Retourne true si le job est actif
	 * @param jobStatus
	 */
	export function isJobAlive(jobStatus: EJobStatus): boolean {
		return (jobStatus == EJobStatus.waiting || jobStatus == EJobStatus.launched || jobStatus == EJobStatus.pending) ? true : false;
	}

	/**
	 * Retourne true si le job est terminé
	 * @param jobStatus
	 */
	export function isJobFinished(jobStatus: EJobStatus): boolean {
		return (jobStatus == EJobStatus.done || jobStatus == EJobStatus.failed) ? true : false;
	}


	/**
	 * Recherche les jobs parmi ceux dont le user courant est le créateur
	 * @see  eu.scenari.core.service.executor.SvcExecutorDialog.CDACTION_FindMyJobs
	 * @param executor
	 * @param criterions
	 */
	export async function findMyJobs(executor: ExecutorSrv, criterions: JJobCriterion): Promise<JListJobs> {
		return executor.config.executorUrl.fetchJson<JListJobs>(IO.qs("cdaction", "FindMyJobs", "criterions", criterions ? CDM.stringify(criterions) : null));
	}

	/**
	 * Recherche les jobs
	 * @see  eu.scenari.core.service.executor.SvcExecutorDialog.CDACTION_FindJobs
	 * @param executor
	 * @param criterions
	 */
	export async function findJobs(executor: ExecutorSrv, criterions?: JJobCriterion): Promise<JListJobs> {
		return executor.config.executorUrl.fetchJson<JListJobs>(IO.qs("cdaction", "FindJobs", "criterions", criterions ? CDM.stringify(criterions) : null));
	}

	/**
	 * Retourne les propriétés complètes d'un job
	 * @see  eu.scenari.core.service.executor.SvcExecutorDialog.CDACTION_GetJob
	 * @param executor
	 * @param jobId : ID du job concerné
	 */
	export async function getJob(executor: ExecutorSrv, jobId: string): Promise<JJob> {
		return executor.config.executorUrl.fetchJson<JJob>(IO.qs("cdaction", "GetJob", "jobId", jobId));
	}

	/**
	 * Retourne les stats sur les jobs (en cours et en erreur)
	 * @see  eu.scenari.core.service.executor.SvcExecutorDialog.CDACTION_GetStats
	 * @param executor
	 */
	export async function getStats(executor: ExecutorSrv): Promise<JJobsStats> {
		return executor.config.executorUrl.fetchJson<JJobsStats>(IO.qs("cdaction", "GetStats"));
	}

	/**
	 * Gèle ou relance (dégèle) l'executor.
	 * @see  eu.scenari.core.service.executor.SvcExecutorDialog.CDACTION_FreezeExecutor
	 * @param executor
	 */
	export async function freezeExecutor(executor: ExecutorSrv, param: boolean | number): Promise<JJobsStats> {
		return executor.config.executorUrl.fetchJson<JJobsStats>(IO.qs("cdaction", "FreezeExecutor", "param", param), {method: "POST"});
	}

	/**
	 * Création d'un Job
	 * @see  eu.scenari.core.service.executor.SvcExecutorDialog.CDACTION_CreateJob
	 * @param executor
	 * @param jobFactoryCode
	 * @param jobDatas
	 * @param plannedDate
	 */
	export async function createJob(executor: ExecutorSrv, jobFactoryCode: string, jobDatas?: Jsonisable, plannedDate?: number): Promise<JJob> {
		const formData = new FormData();
		if (plannedDate) formData.append('date', plannedDate.toString());
		if (jobDatas) formData.append('jobDatas', CDM.stringify(jobDatas));
		return executor.config.executorUrl.fetchJson<JJob>(IO.qs("cdaction", "CreateJob", "param", jobFactoryCode), {method: 'POST', body: formData});
	}

	/**
	 * Force l'exécution d'un job existant
	 * @see  eu.scenari.core.service.executor.SvcExecutorDialog.CDACTION_ForceExecuteJob
	 * @param executor
	 * @param jobId : ID du job concerné
	 */
	export async function forceExecuteJob(executor: ExecutorSrv, jobId: string): Promise<JJob> {
		return executor.config.executorUrl.fetchJson<JJob>(IO.qs("cdaction", "ForceExecuteJob", "jobId", jobId), {method: "POST"});
	}

	/**
	 * Planifie un job existant pour une (ré)exécution à venir
	 * @see  eu.scenari.core.service.executor.SvcExecutorDialog.CDACTION_ScheduleJob
	 * @param executor
	 * @param jobId : ID du job concerné
	 * @param plannedDate : date de planification
	 */
	export async function scheduleJob(executor: ExecutorSrv, jobId: string, plannedDate: number): Promise<JJob> {
		return executor.config.executorUrl.fetchJson<JJob>(IO.qs("cdaction", "ScheduleJob", "jobId", jobId, "date", plannedDate), {method: "POST"});
	}

	/**
	 * Supprime définitivement d'un job
	 * @see  eu.scenari.core.service.executor.SvcExecutorDialog.CDACTION_DeleteJob
	 * @param executor
	 * @param jobId : ID du job concerné
	 */
	export async function deleteJob(executor: ExecutorSrv, jobId: string): Promise<boolean> {
		let resp = await executor.config.executorUrl.fetch(IO.qs("cdaction", "DeleteJob", "jobId", jobId), null, {method: "POST"});
		return resp.ok;
	}

	/**
	 * Déplace un job dans la file d'attente
	 * @see  eu.scenari.core.service.executor.SvcExecutorDialog.CDACTION_MoveJobInQueue
	 * @param executor
	 * @param jobId : ID du job concerné
	 * @param position
	 * @return -1 si le job n'est pas dans le statut {@link EJobStatus#waiting}, sinon offset de la nouvelle position dans la file d'attente
	 */
	export async function moveJobInQueue(executor: ExecutorSrv, jobId: string, position: 'top' | 'bottom' | number): Promise<number> {
		let resp = await executor.config.executorUrl.fetchJson<{ newOffsetInQueue: number }>(IO.qs("cdaction", "MoveJobInQueue", "jobId", jobId, "jobDatas", position), {method: "POST"});
		return resp.newOffsetInQueue;
	}

}

// ###
// # Job : représentation UI d'un job, et fonction de création
type dynBoolean<C> = (job: JJob) => boolean;

export interface UiJobFactory<C extends IRegPointer<IExecutorInUniversePointer & IUiEnv>, D extends IJobBuilderActionContext> {
	/** Action de création du Job */
	jobBuilder?: JobBuilderAction<D>,

	/** Retourne true si cet objet peut s'appliquer au Job */
	matchJob(job: JJob): boolean

	/** Construit une forme HTML lisible du job */
	buildJobDetails(target: HTMLElement, ctx: C, job: JJob, options?: any): Promise<void>,
}


export class UiJobFactory<C extends IRegPointer<IExecutorInUniversePointer & IUiEnv>, D extends IJobBuilderActionContext> extends Signboard<D> implements UiJobFactory<C, D> {
	protected _matchJob: dynBoolean<C>;

	protected _uiJobSng: string[]

	protected _jobFactoryCode: string

	/**
	 * Signature UI du job
	 * @param uiJobSng
	 */
	setUiJobSng(uiJobSng: string[]): this {
		this._uiJobSng = uiJobSng;
		return this;
	}

	getUiJobSng(): string[] {
		return this._uiJobSng;
	}

	/**
	 * Factory serveur du job
	 * @param factoryCode
	 */
	setJobFactoryCode(factoryCode: string): this {
		this._jobFactoryCode = factoryCode;
		return this;
	}

	getJobFactoryCode(): string {
		return this._jobFactoryCode || this.getId();
	}

	/** Retourne true si cet objet peut s'appliquer au Job */
	matchJob(job: JJob): boolean {
		if (this._matchJob)
			return this._matchJob(job);
		else if (job.jobSgn && this._uiJobSng) {
			let patterns: string[] = [];
			this._uiJobSng.forEach((str, pos) => {
				patterns.push(LANG.escape4Regexp(str));
				patterns.push(".*#" + LANG.escape4Regexp(str) + "\\b.*");
			});
			let regExp = new RegExp(`.*(${patterns.join('|')}).*`);
			return job.jobSgn.match(regExp) ? true : false;
		}
		return false;
	}

	setMatchJob(matchJob: dynBoolean<C>): this {
		this._matchJob = matchJob;
		return this;
	}

	setJobBuilder(jobBuilder: JobBuilderAction<D>): this {
		this.jobBuilder = jobBuilder;
		return this;
	}

	getJobBuilder(): JobBuilderAction<D> {
		return this.jobBuilder;
	}

	async buildJobDetails(target: HTMLElement, ctx: C, job: JJob, options?: any): Promise<void> {
		let jobId = job.id;
		let jobLabel = this.getLabel({job: job} as D);
		target.appendChild(<h1>{`[Traitement ${jobId}] ${jobLabel}]`}</h1>);
		await this.buildLongDescForJob(ctx, job, target.appendChild(<div/>));
		target.appendChild(UiJobFactory.buildJobPropsDetails(job));
		target.appendChild(UiJobFactory.buildJobTriesDetails(job, ctx.reg));
	}

	/**
	 * Construit une forme lisible UI des paramètres de ce job
	 * @param ctx
	 * @param form
	 */
	async buildLongDescForJob(ctx: C, job: JJob, target: HTMLElement): Promise<void> {
		const desc = this.getDescription({job: job} as D);
		if (desc)
			target.appendChild(<div class="header">{desc}</div>);
	}

	static buildJobTitleDetails(job: JJob): HTMLElement {
		if (job) {
			let jobId = job.id;
			return <h1>{`[Traitement ${jobId}]`}</h1>;
		}
	}

	static buildJobPropsDetails(job: JJob): HTMLElement {
		if (job.jobProps) {
			return <div>
				<h2>Propriétés</h2>
				<div class="content code">
					{JSON.stringify(job.jobProps)}
				</div>
			</div>
		}
	}

	static buildJobTriesDetails(job: JJob, reg: IReg<ICoreUniverseEnv>): HTMLElement {
		if (job.tries) {
			let grid = new GridSmall().initialize({
				selType: 'none',
				columnDefs: [new GridColDef('started')
					.setLabel("Début")
					.setFlex('2rem', 1, 1)
					.setCellBuilder(new CellBuilderDate("started").setCellClass("center").setOptions({year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit"})),
					new GridColDef('finished')
						.setLabel("Fin")
						.setFlex('2rem', 1, 1)
						.setCellBuilder(new CellBuilderDate("finished").setCellClass("center").setOptions({year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit"})),
					new GridColDef('by')
						.setLabel("Exécuteur")
						.setFlex("1rem", 1, 1)
						.setCellBuilder(new CellBuilderAccount(reg, "by", true)),
					new GridColDef('result')
						.setLabel("Résultat")
						.setFlex('2rem', 1, 1)
						.setCellBuilder(new CellBuilderEnum('status', new Map([
							[EJobSucess.attemptFailed, "Tentative en échec"],
							[EJobSucess.finalFailure, "En échec"],
							[EJobSucess.finished, "Terminé"],
						])).setCellClass("center").override("_getValue", function (this: CellBuilderEnum, row: GridDataRowJson<JJobPropsTry>) {
							let success = row.getData("result") ? row.getData("result").success : null;
							return (success && this.mappingTable.has(success)) ? this.mappingTable.get(success) : success;
						})),
					new GridColDef('status')
						.setLabel("Statut")
						.setFlex('2rem', 1, 1)
						.setCellBuilder(new CellBuilderEnum('status', new Map([
							[EJobStatus.waiting, "En attente"],
							[EJobStatus.launched, "Chargé"],
							[EJobStatus.pending, "En cours d'exécution"],
							[EJobStatus.done, "Terminé"],
							[EJobStatus.failed, "En échec"],
							[EJobStatus.planned, "Planifié"],
						])).setCellClass("center")),
					new GridColDef('actions')
						.setLabel("")
						.setSortable(false)
						.setCellBuilder(new CellBuilderBarActions<IRegPointer<ICoreUniverseEnv> & IJobPropsTryActionContext>('actions', [
							new Action()
								.setLabel("Détails techniques")
								.setIcon("/@skin@/core/actions/jobs/details.svg")
								.setVisible((ctx: IJobPropsTryActionContext) => (ctx.try?.error || ctx.try?.result?.system) ? true : false)
								.setExecute(async (ctx: IJobPropsTryActionContext) => {
									// FIXME : faire un visualiseur JSON (indentation, ...
									if (ctx.try?.error) {
										await ERROR.show("Échec d'exécution du job", new Error(ctx.try.error + "\n" + (ctx.try?.result?.system ? JSON.stringify(ctx.try?.result?.system) : "")));
									} else
										await ERROR.show("Traces techniques d'exécution du job", new Error(JSON.stringify(ctx.try?.result?.system)));
									/*await POPUP.showDialog(<div>{JSON.stringify(ctx.try?.result?.system)}</div>, document.body, {
										titleBar: {barLabel: {label: "Détails techniques de l'exécution"}},
										resizer: {},
										initWidth: '40em',
										initMaxHeight: '80vh',
										fixSize: false
									}).onNextClose();*/
								})
						], {
							reg: reg,
						}).setActionContextMaker((row: GridDataRowJson<JJobPropsTry>, actionContext) => {
							actionContext.try = row.rowDatas;
						}))
				],
				dataHolder: new GridDataHolderJsonArray<JJobPropsTry>(),
				hideHeaders: false,
				skinOver: 'c-uijobfactory-tries/grid',
				hideSortBtns: true,
				emptyBody: (): Node => {
					return <c-msg label="Aucune" level="info"/>
				}
				,
				lineDrawer: {
					redrawLine: (row: GridDataRowJson<JJobPropsTry>, line) => {
						DOM.setAttrBool(line, "data-error", row.getData("status") == EJobStatus.failed || row.getData('result')?.success === EJobSucess.finalFailure);
					}
				}
			} as OGridSmallInit);
			grid.addEventListener("grid-select", (evt) => {
				evt.stopPropagation();
			});

			(grid.dataHolder as GridDataHolderJsonArray<JJobPropsTry>).setDatas(job.tries);

			return <div>
				<h2>Tentatives d'exécution</h2>
				<div class="content">
					{grid}
				</div>
			</div>
		}
	}

}

REG.reg.registerSkin('c-uijobfactory-tries/grid', 1, /* language=CSS */ `
	*[data-error] {
		color: var(--error-color)
	}
`);

/**
 * Action de construction d'un job
 */
export interface IJobBuilderActionContext extends IRegPointer<IExecutorInUniversePointer & IUiEnv> {
	/** Job à éditer, ou à utiliser comme source de création d'un autre job */
	job?: JJob
}

export interface IJobPropsTryActionContext extends IRegPointer<ICoreUniverseEnv & IUiEnv> {
	try?: JJobPropsTry,
}

export class JobBuilderAction<C extends IJobBuilderActionContext> extends Action<C> {

	/** Prop permettant de sérialiser les éventuels propriétés UI */
	static UI_PROPS = "ui-props";

	protected _jobDatasBase: Jsonisable

	protected _planned: null | 'byUser' | dynNumber<C> = null

	protected _plannedByUserPropName: string = "plannedDate"

	protected _jobDialogAreas: (IArea<IJobAreaContext & IControlAreaContext> | Promise<IArea<IJobAreaContext & IControlAreaContext>> | dynAny<C, IArea<IJobAreaContext & IControlAreaContext>>)[]

	constructor(public uiJobFactory: UiJobFactory<any, any>) {
		super();
		// - jobDataBuilders
		this.addJobDataBuilder('_jobDatasBase', async (datasPending, jobsParameters, ctx) => {
			if (this.getBaseJobDatas()) Object.assign(datasPending, this.getBaseJobDatas())
		}, 0);
		this.addJobDataBuilder('_uiProps', async (datasPending, jobsParameters, ctx) => {
			const dialogAreas = await this.getJobDialogAreas(ctx);
			if (dialogAreas) {
				datasPending[JobBuilderAction.UI_PROPS] = {};
				dialogAreas.forEach((area: IArea<any>) => {if (area.isAvailable(ctx)) datasPending[JobBuilderAction.UI_PROPS][area.getId()] = jobsParameters[area.getId()]})
			}
		}, 0);
		this.addJobDataBuilder('_jobsParameters', (datasPending, jobsParameters, ctx) => Object.assign(datasPending, jobsParameters), 0);
	}

	getDescription(ctx: C): string {
		return this._description ? super.getDescription(ctx) : this.uiJobFactory.getDescription(ctx);
	}

	getLabel(ctx: C): string {
		return this._label ? super.getLabel(ctx) : this.uiJobFactory.getLabel(ctx);
	}

	getIcon(ctx: C): string {
		return super.getIcon(ctx) ? super.getIcon(ctx) : this.uiJobFactory.getIcon(ctx);
	}

	isVisible(ctx: C): boolean {
		if (!this.uiJobFactory.isVisible(ctx)) return false;
		return super.isVisible(ctx);
	}

	isEnabled(ctx: C): boolean {
		if (!this.uiJobFactory.isEnabled(ctx)) return false;
		return super.isEnabled(ctx);
	}

	/**
	 * Affectation de paramètres fixes du job, définis par défaut
	 * @param jobDatas
	 */
	addBaseJobDatas(jobDatas?: Jsonisable): this {
		this._jobDatasBase = Object.assign(this._jobDatasBase || {}, jobDatas);
		return this;
	}

	getBaseJobDatas(): Jsonisable | null {
		return this._jobDatasBase;
	}

	/**
	 * Dialogue de confirmation du Job, avec affichage d'un formulaire permettant de saisir des paramètres
	 * dont les valeurs sont retournées en résultat de la promise : planification, ...
	 * Retourne : {} ou null signifiant une volonté d'annulation du traitement
	 */
	async jobDialog(ctx: C): Promise<Jsonisable | null> {
		const label = this.getLabel(ctx);
		let form: HTMLFormElement;
		let msg = <div>
			<h4>{`Traitement : "${label}"`}</h4>
		</div>;
		const dialogAreas = await this.getJobDialogAreas(ctx);
		if (dialogAreas || this.getPlanned(ctx) === "byUser") {
			form = msg.appendChild(<form id="form" autocomplete="off">
				<fieldset id="fieldset" area-ids="*"/>
			</form> as HTMLFormElement)
			let areas: IArea<IJobAreaContext & IControlAreaContext>[] = [];
			if (dialogAreas)
				areas = areas.concat(dialogAreas);
			if (this.getPlanned(ctx) === "byUser")
				areas.push(new JobPlanificatorInputArea(this._plannedByUserPropName));
			await AREAS.applyLayout(form, areas, {
				buildControlLabel: true,
				reg: ctx.reg,
				uiJobFactory: this.uiJobFactory,
			} as IJobAreaContext, true);
			if (ctx.job && ctx.job.jobProps && ctx.job.jobProps[JobBuilderAction.UI_PROPS])
				FORMS.jsonToForm(ctx.job.jobProps[JobBuilderAction.UI_PROPS], form, false, true);
			form.checkValidity();
		}

		if (await POPUP.confirm(<div style="display: contents;"><ShadowJsx skin="form-control-areas" skinOver="job-create-form">{msg}</ShadowJsx></div>, null, {
			resizer: {},
			okLbl: "Créer",
			titleBar: {barLabel: {label: "Nouveau traitement"},}
		})) {
			// FIXME : controle de validié du formulaire
			return form ? FORMS.formToJson(form) : [];
		} else
			return null;
	}

	/**
	 * Exploitation des areas dans un formulaire libre (visualisation, ...)
	 * @param ctx
	 * @param form
	 */

	/*
	async buildAreasForm(ctx: C, form : HTMLFormElement) : Promise<void>{
		if (this.getJobDialogAreas()) {
			let areas: IArea<IJobAreaContext & IControlAreaContext>[] = await Promise.all(this.getJobDialogAreas());
			await AREAS.applyLayout(form, areas, {
				buildControlLabel: true,
				reg: ctx.reg,
				uiJobFactory: this.uiJobFactory,
			} as IJobAreaContext, true);
		}
	}
	*/

	/**
	 * Areas permettant de définir le UI du dialogue
	 */
	addJobDialogAreas(params: (IArea<IJobAreaContext & IControlAreaContext> | Promise<IArea<IJobAreaContext & IControlAreaContext>> | dynAny<C, IArea<IJobAreaContext & IControlAreaContext>>)[]): this {
		if (!this._jobDialogAreas) this._jobDialogAreas = [];
		this._jobDialogAreas = this._jobDialogAreas.concat(params);
		return this;
	}

	async getJobDialogAreas(ctx: C): Promise<IArea<IJobAreaContext & IControlAreaContext>[] | null> {
		if (!this._jobDialogAreas)
			return null;
		const areas = await Promise.all(this._jobDialogAreas);
		const resolvedAreas: IArea<IJobAreaContext & IControlAreaContext>[] = [];
		await Promise.all(areas.map(async (area, ind) => {
			if (typeof area === "function") {
				const result = await Promise.resolve(area.call(this, ctx));
				if (result) resolvedAreas.push(result);
			} else
				resolvedAreas.push(area)
		}));
		return resolvedAreas;
	}

	/**
	 * Construit les datas du job
	 */
	_jobDatasBuilder: Map<string, { sort: number, fct: (datasPending: Jsonisable, jobsParameters: Jsonisable, ctx: C) => Promise<void> }> = new Map()

	addJobDataBuilder(id: string, fct: (datasPending: Jsonisable, jobsParameters: Jsonisable, ctx: C) => Promise<void>, sort: number): this {
		this._jobDatasBuilder.set(id, {sort: sort || 0, fct});
		return this;
	}

	async buildJobDatas(jobsParameters: Jsonisable, ctx: C): Promise<Jsonisable> {
		let jobDatas: Jsonisable = {};
		const dataBuilders = Array.from(this._jobDatasBuilder.values()).sort((a, b) => a.sort - b.sort);
		await Promise.all(dataBuilders.map(async (bd) => {
			await bd.fct.call(this, jobDatas, jobsParameters, ctx);
		}));
		return jobDatas;
	}


	/**
	 * Vérifie la validité des params construits
	 * NOTE : cette fonction peut afficher un tooltip de notification
	 */

	_jobDatasValidity: Set<(datas: Jsonisable, ctx: C) => Promise<boolean>> = new Set()

	addJobDataValidity(fct: (datas: Jsonisable, ctx: C) => Promise<boolean>): this {
		this._jobDatasValidity.add(fct);
		return this;
	}

	async checkJobDatasValidity(datas: Jsonisable, ctx: C): Promise<boolean> {
		const fcts = Array.from(this._jobDatasValidity.values());
		for (let i = 0; i <= fcts.length; i++) {
			if (fcts[i] && !await fcts[i].call(this, datas, ctx)) return false;
		}
		return true;
	}

	/**
	 * Stratégie de spécif de la planification
	 * @param planned
	 */
	setPlanned(planned: null | 'byUser' | dynNumber<C>): this {
		this._planned = planned;
		return this;
	}

	getPlanned(ctx: C): null | 'byUser' | dynNumber<C> {
		if (!ctx.reg.env.universe.executor.hasFeature("scheduling"))
			return null;
		return this._planned;
	}

	async execute(ctx: C, ev?: Event): Promise<JJob | null> {
		try {
			const jobsParameters = await this.jobDialog(ctx);
			if (jobsParameters !== null) {
				// Planned
				let plannedDate: number;
				const planned = this.getPlanned(ctx);
				if (planned === "byUser") {
					plannedDate = jobsParameters[this._plannedByUserPropName] ? jobsParameters[this._plannedByUserPropName].date : null;
					jobsParameters[this._plannedByUserPropName] = undefined; // clean
				} else if (typeof planned === "function") {
					plannedDate = planned.call(this, ctx);
				}
				// Params
				let jobDatas: Jsonisable;
				jobDatas = await this.buildJobDatas(jobsParameters, ctx);
				if (jobDatas && jobDatas.rulesSgn === undefined) jobDatas.rulesSgn = this.uiJobFactory.getUiJobSng();
				//console.log("Create JOB ::: ", plannedDate, jobDatas);
				if (await this.checkJobDatasValidity(jobDatas, ctx))
					return await EXECUTOR.createJob(ctx.reg.env.universe.executor, this.uiJobFactory.getJobFactoryCode(), jobDatas, plannedDate);
			}
		} catch (e) {
			await ERROR.report(`La création du traitement n'a pas pu aboutir. Veuillez réessayer ultérieurement.`, e);
		}
	}

	override(name: keyof Action<C> | 'buildJobDatas', val: any): this {
		return super.override(name as any, val);
	}
}

REG.reg.registerSkin("job-create-form", 1, /* language=CSS */ `
	:host > div{
		display: contents;
	}
	
	form {
		display: flex;
		flex: 1;
		min-width: 0;
		min-height: 0;
	}
	
	fieldset {
		display: flex;
		flex: 1;
		min-width: 0;
		min-height: 0;
		flex-direction: column;
		border: none;
		padding: 0;
		margin: 0;
		min-width: 0;
	}

	*[area-id="_processings"], *[area-id="_views"] {
		flex: 1;
	}

`);

type dynNumber<C> = (this: JobBuilderAction<any>, ctx: C) => number;

export interface IJobAreaContext extends IControlAreaContext, IRegPointer<IExecutorInUniversePointer & IUiEnv> {
	/** Ui Factory de ce job */
	uiJobFactory: UiJobFactory<any, any>
	/** Job en cours d'édition, ou nul si création */
	job?: JJob,
}
